import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Lock, Mail } from "lucide-react";
import { QDMonogram } from "../components/Header";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && user.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) navigate("/admin");
    else setError(res.error || "Login failed");
  };

  return (
    <div className="min-h-screen w-full flex">
      {/* Left feature panel */}
      <div className="hidden lg:flex w-1/2 relative bg-[#1A1A1A] text-white overflow-hidden">
        <img
          src="https://images.pexels.com/photos/8425444/pexels-photo-8425444.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/70" />
        <div className="relative z-10 flex flex-col justify-between p-14 w-full">
          <Link to="/" className="font-serif-display text-3xl flex items-center gap-3">
            <QDMonogram size={40} variant="light" />
            <span>Queens <span className="italic font-light">Daily</span></span>
          </Link>
          <div>
            <p className="overline text-white/70 mb-4">Editor Portal</p>
            <h1 className="font-serif-display text-5xl xl:text-6xl leading-[1.05] tracking-tight">
              Write the
              <br /> next chapter.
            </h1>
            <p className="mt-6 text-white/75 max-w-md font-sans-modern">
              Curate the deck. Publish stories that move our readers.
            </p>
          </div>
        </div>
      </div>

      {/* Right login form */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-12 bg-[#FDFBF7]">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden block mb-12 text-[#1A1A1A] flex items-center gap-3">
            <QDMonogram size={40} />
            <span className="font-serif-display text-3xl">Queens <span className="italic font-light">Daily</span></span>
          </Link>
          <p className="overline text-[#76746F] mb-3">Editor sign in</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight mb-10">
            Welcome back.
          </h2>

          <form onSubmit={submit} className="space-y-6" data-testid="admin-login-form">
            <div>
              <label className="overline text-[#76746F] block mb-2">Email</label>
              <div className="flex items-center gap-3 border-b border-black/30 focus-within:border-black transition-colors py-2">
                <Mail size={16} strokeWidth={1.5} className="text-[#76746F]" />
                <input
                  data-testid="email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@queensdaily.in"
                  className="flex-1 bg-transparent outline-none font-sans-modern text-base"
                />
              </div>
            </div>

            <div>
              <label className="overline text-[#76746F] block mb-2">Password</label>
              <div className="flex items-center gap-3 border-b border-black/30 focus-within:border-black transition-colors py-2">
                <Lock size={16} strokeWidth={1.5} className="text-[#76746F]" />
                <input
                  data-testid="password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="flex-1 bg-transparent outline-none font-sans-modern text-base"
                />
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-[#B33A3A] font-sans-modern">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="w-full bg-[#1A1A1A] text-white py-4 rounded-full hover:bg-[#D4A373] hover:text-white transition-colors font-sans-modern text-sm tracking-wide disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="mt-10 text-xs text-[#76746F]">
            <Link to="/" className="hover:underline underline-offset-4">
              ← Back to Queens Daily
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
